import { useMemo } from 'react';
import { format, isSameDay, differenceInMonths, parseISO, startOfWeek, endOfWeek, isValid } from 'date-fns';
import { transformToISO } from 'utils/dateformat.utils';

type GroupingStrategy = 'daily' | 'weekly' | 'monthly' | null;

interface TimeGroupInfo {
    groupKey: string;
    displayName: string;
    callDate: Date;
}

interface UseChartDateRangeParams {
    dateRangeType?: string;
    startDate?: Date;
    endDate?: Date;
}

interface UseChartDateRangeReturn {
    dateRangeDisplay: string;
    shouldGroupByType: boolean;
    groupingStrategy: GroupingStrategy;
    getTimeGroupInfo: (dateString: string, strategy: GroupingStrategy) => TimeGroupInfo | null;
    initializeTimeGroups: (
        allCategories: string[],
        normalizeFunction?: (category: string) => string
    ) => (groupKey: string, displayName: string) => Record<string, string | number>;
    isStatusView: boolean;
    isDispositionView: boolean;
}

/**
 * Custom hook for handling common chart date range logic
 * Provides date range display, grouping strategy, and time grouping utilities
 */
export const useChartDateRange = ({
    dateRangeType,
    startDate,
    endDate,
}: UseChartDateRangeParams): UseChartDateRangeReturn => {
    // Generate date range display string
    const dateRangeDisplay = useMemo(() => {
        if (dateRangeType === 'today') {
            return 'Today';
        } else if (dateRangeType === 'yesterday') {
            return 'Yesterday';
        } else if (dateRangeType === 'last7Days') {
            return 'Last 7 Days';
        } else if (dateRangeType === 'last30Days') {
            return 'Last 30 Days';
        } else if (dateRangeType === 'custom' && startDate && endDate) {
            if (isSameDay(startDate, endDate)) {
                return format(startDate, 'MMM d, yyyy');
            } else {
                return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
            }
        }
        return '';
    }, [dateRangeType, startDate, endDate]);

    // Determine if we should group by type (status/disposition) or by time periods
    const shouldGroupByType = useMemo(() => {
        return dateRangeType === 'today' || dateRangeType === 'yesterday';
    }, [dateRangeType]);

    // Determine time-based grouping strategy
    const groupingStrategy = useMemo((): GroupingStrategy => {
        if (shouldGroupByType) {
            return null; // Not needed for type-based grouping
        }

        if (dateRangeType === 'custom' && startDate && endDate) {
            // Calculate the difference in months for custom ranges
            const monthsDiff = differenceInMonths(endDate, startDate);

            if (monthsDiff > 2) {
                return 'monthly';
            } else if (monthsDiff >= 1) {
                return 'weekly';
            } else {
                return 'daily';
            }
        } else if (dateRangeType === 'last7Days') {
            return 'daily';
        } else if (dateRangeType === 'last30Days') {
            return 'weekly';
        } else {
            return 'monthly';
        }
    }, [dateRangeType, startDate, endDate, shouldGroupByType]);

    // Utility function to get time group info for a given date
    const getTimeGroupInfo = useMemo(() => {
        return (dateString: string, strategy: GroupingStrategy): TimeGroupInfo | null => {
            if (!dateString || !strategy) return null;

            try {
                const isoDateString = transformToISO(dateString);
                const callDate = parseISO(isoDateString);

                // Skip invalid dates
                if (!isValid(callDate)) {
                    console.warn('Invalid date in chart data:', dateString);
                    return null;
                }

                let groupKey = '';
                let displayName = '';

                if (strategy === 'daily') {
                    // Group by individual days
                    groupKey = format(callDate, 'yyyy-MM-dd');
                    displayName = format(callDate, 'MMM d');
                } else if (strategy === 'weekly') {
                    // Group by weeks
                    const startOfWeekDate = startOfWeek(callDate);
                    const endOfWeekDate = endOfWeek(callDate);

                    groupKey = format(startOfWeekDate, 'yyyy-MM-dd');
                    displayName = `${format(startOfWeekDate, 'MM/dd')} - ${format(endOfWeekDate, 'MM/dd')}`;
                } else if (strategy === 'monthly') {
                    // Group by months
                    groupKey = format(callDate, 'yyyy-MM');
                    displayName = format(callDate, 'MMM yyyy');
                }

                return { groupKey, displayName, callDate };
            } catch (error) {
                console.error('Error processing date in chart:', error, dateString);
                return null;
            }
        };
    }, []);

    // Utility function to initialize time groups with all possible categories
    const initializeTimeGroups = useMemo(() => {
        return (allCategories: string[], normalizeFunction?: (category: string) => string) => {
            return (groupKey: string, displayName: string): Record<string, string | number> => {
                const timeGroup: Record<string, string | number> = {
                    name: displayName,
                    sortKey: groupKey,
                    total: 0,
                };

                // Initialize all category counts to 0
                allCategories.forEach((category) => {
                    const normalized = normalizeFunction ? normalizeFunction(category) : category;
                    timeGroup[normalized] = 0;
                });

                return timeGroup;
            };
        };
    }, []);

    return {
        dateRangeDisplay,
        shouldGroupByType,
        groupingStrategy,
        getTimeGroupInfo,
        initializeTimeGroups,
        // Legacy aliases for backward compatibility
        isStatusView: shouldGroupByType,
        isDispositionView: shouldGroupByType,
    };
};
